import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { ColumnDef } from '@tanstack/react-table';
import { linkOptions } from '@tanstack/react-router';
import { useUserRoles } from '@anytag/auth';
import { useTableInputCursor } from '@anytag/shared-hooks';
import { formatNumber, formatPercent } from '@anytag/shared-utils';
import { Anchor, TableCell, MediaPreview } from '@anytag/shared-atoms';
import { YoutubeCMSChannelRevenueFormValues } from '../types';
import { YoutubeCMSChannel, YoutubeCMSRevenueType } from '../../types';

interface Props {
  modifiable?: boolean;
  type: YoutubeCMSRevenueType;
}

export const useYoutubeCMSChannelsTable = ({ type, modifiable = true }: Props) => {
  const { t } = useTranslation();
  const { isAdminStaff } = useUserRoles();
  const { control } = useFormContext<YoutubeCMSChannelRevenueFormValues>();
  const { handleInputFocus, handleInputBlur } = useTableInputCursor<YoutubeCMSChannelRevenueFormValues>();

  const isConfirmedRevenue = type === YoutubeCMSRevenueType.CONFIRMED_REVENUE;
  const isEstimatedRevenue = type === YoutubeCMSRevenueType.ESTIMATED_REVENUE;
  const canEditShareRate = isEstimatedRevenue && modifiable;
  const columns: ColumnDef<YoutubeCMSChannel>[] = [
    {
      accessorKey: 'channelName',
      meta: { sticky: true },
      header: t<string>('HeaderColumn.Channel name'),
      cell: ({ row }) => (
        <div className="flex min-w-50">
          <MediaPreview className="size-8 rounded-sm" src={row.original.avatar} />
          <div className="ml-2 leading-[100%]">
            <Anchor
              lines={2}
              className="text-base"
              label={row.original.channelName || '-'}
              disabled={isConfirmedRevenue || !row.original.id}
              {...(isEstimatedRevenue && row.original.id
                ? linkOptions({
                    to: '/youtube-cms/revenue/$cmsChannelId',
                    params: { cmsChannelId: row.original.id.toString() || '' }
                  })
                : undefined)}
            />
            <p className="mt-1 text-xxs text-tertiary">
              {t('id')}:&nbsp;
              <span className="select-all">{row.original.channelId}</span>
            </p>
          </div>
        </div>
      )
    },
    {
      accessorKey: 'rpm',
      header: t<string>('HeaderColumn.Estimated RPM'),
      meta: { align: 'right', hidden: !isAdminStaff || isConfirmedRevenue },
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.rpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'taRpm',
      meta: { align: 'right', hidden: isConfirmedRevenue },
      header: t<string>('HeaderColumn.Estimated talent agency RPM'),
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.taRpm)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'partnerRevenue',
      meta: { align: 'right', hidden: !isAdminStaff },
      header: t<string>(
        isConfirmedRevenue ? 'HeaderColumn.Confirmed partner revenue' : 'HeaderColumn.Estimated partner revenue'
      ),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.partnerRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taRevenue',
      meta: { align: 'right', hidden: isConfirmedRevenue },
      header: t<string>('HeaderColumn.Estimated talent agency revenue'),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taRevenue)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'shareCreator',
      meta: { align: 'right' },
      header: t<string>(
        isConfirmedRevenue ? 'HeaderColumn.Confirmed share to creator' : 'HeaderColumn.Estimated share to creator'
      ),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.shareCreator)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'profit',
      meta: { align: 'right', hidden: !isAdminStaff },
      header: t<string>(isConfirmedRevenue ? 'HeaderColumn.Confirmed gross profit' : 'HeaderColumn.Estimated GP'),
      cell: ({ row }) => <TableCell.Number value={formatNumber(row.original.profit)} unit={t(row.original.currency)} />
    },
    {
      accessorKey: 'taProfit',
      meta: { align: 'right', hidden: isConfirmedRevenue },
      header: t<string>('HeaderColumn.Estimated talent agency GP'),
      cell: ({ row }) => (
        <TableCell.Number value={formatNumber(row.original.taProfit)} unit={t(row.original.currency)} />
      )
    },
    {
      accessorKey: 'taShareRate',
      header: t<string>('HeaderColumn.Talent agency margin rate'),
      meta: { align: 'right', hidden: !isAdminStaff || isConfirmedRevenue },
      cell: ({ row }) => <TableCell.Number value={formatPercent(row.original.taShareRate, false)} unit="%" />
    },
    {
      accessorKey: 'shareCreatorRate',
      meta: { align: 'right' },
      header: t<string>('HeaderColumn.Creator margin rate'),
      cell: ({ row }) => (
        <TableCell.ControlledRevenueInput
          name={`${row.original.channelId}-shareRate` satisfies keyof YoutubeCMSChannelRevenueFormValues}
          rightSection="%"
          placeholder="0"
          control={control}
          className="min-w-42.5"
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          disabled={!canEditShareRate}
          updatedDate={row.original.lastUpdatedDate}
          updatedTitle={t('Last Updated').toLowerCase()}
        />
      )
    }
  ];

  return { columns };
};
